import axios from 'axios'
import { VueAxios } from './axios'
import message from 'ant-design-vue/es/message'
import { getApiHeader, getToken, setToken, removeToken } from '@/api/auth'

const request = axios.create({
  baseURL: window.g.baseUrl,
  timeout: 30000
})

const REFRESH_TOKEN_URL = 'api/v1/user/refreshLogin'
let refreshTokenPromise = null

function isRefreshRequest(config) {
  return String(config && config.url ? config.url : '').includes(REFRESH_TOKEN_URL)
}

function isCanceledRequest(error) {
  return axios.isCancel(error) || error && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')
}

function shouldSkipGlobalErrorMessage(config) {
  return !!(config && config.skipGlobalErrorMessage)
}

function redirectToLogin() {
  removeToken()
  setTimeout(() => {
    window.location.reload()
  }, 1000)
}

function refreshAccessToken() {
  const token = getToken()
  if (!token) {
    return Promise.reject(new Error('Missing token'))
  }
  if (!refreshTokenPromise) {
    const parameter = { uri: '' }
    const headers = getApiHeader(parameter, REFRESH_TOKEN_URL)
    headers.Authorization = 'Bearer ' + token
    headers['Access-Token'] = token

    refreshTokenPromise = axios({
      baseURL: window.g.baseUrl,
      url: REFRESH_TOKEN_URL,
      method: 'post',
      data: parameter,
      headers: headers,
      timeout: 30000
    }).then(response => {
      const data = response && response.data ? response.data : {}
      const newToken = data.data && data.data.token ? data.data.token : ''
      if (data.code == 200 && newToken) {
        setToken(newToken)
        return newToken
      }
      return Promise.reject(response)
    }).finally(() => {
      refreshTokenPromise = null
    })
  }
  return refreshTokenPromise
}

function retryWithNewToken(config, token) {
  config.headers = config.headers || {}
  config.headers.Authorization = 'Bearer ' + token
  config.headers['Access-Token'] = token
  config.__tokenRefreshed = true
  return request(config)
}

function refreshAndRetry(config, fallbackError) {
  if (!config || config.__tokenRefreshed || isRefreshRequest(config) || !getToken()) {
    message.error('授权验证失败')
    redirectToLogin()
    return Promise.reject(fallbackError)
  }
  return refreshAccessToken()
    .then(token => retryWithNewToken(config, token))
    .catch(() => {
      message.error('授权验证失败')
      redirectToLogin()
      return Promise.reject(fallbackError)
    })
}

function getErrorMessage(error) {
  if (error && error.code === 'ECONNABORTED') {
    return '请求超时，请稍后重试'
  }
  if (error && error.response) {
    const data = error.response.data || {}
    return data.msg || data.message || error.response.statusText || `请求失败(${error.response.status})`
  }
  if (error && error.message && error.message.includes('Network Error')) {
    return '网络异常，请检查网络或服务器'
  }
  return error && error.message ? error.message : '请求失败，请稍后重试'
}

const errorHandler = (error) => {
  if (isCanceledRequest(error)) {
    return Promise.reject(error)
  }
  const config = error && error.config ? error.config : {}
  if (error.response) {
    if (error.response.status === 401) {
      if (isRefreshRequest(config)) {
        return Promise.reject(error)
      }
      return refreshAndRetry(config, error)
    }
    if (!shouldSkipGlobalErrorMessage(config)) {
      message.error(getErrorMessage(error))
    }
  } else if (!shouldSkipGlobalErrorMessage(config)) {
    message.error(getErrorMessage(error))
  }
  return Promise.reject(error)
}

request.interceptors.request.use(config => {
  const token = getToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers['Access-Token'] = token
    config.headers.Authorization = 'Bearer ' + token
  }
  return config
}, errorHandler)

request.interceptors.response.use((response) => {
  const data = response && response.data ? response.data : {}
  if ((data.code == 401 || data.code == 10104) && !isRefreshRequest(response.config || {})) {
    return refreshAndRetry(response.config || {}, response)
  }
  if ('success' in data) {
    if (!data.success) {
      if (shouldSkipGlobalErrorMessage(response.config || {})) {
        return Promise.reject(response)
      }
      message.error(data.msg || data.message || '请求失败，请稍后重试')
      return Promise.reject(response)
    }
    return data.data
  }
  return response
}, errorHandler)

const installer = {
  vm: {},
  install (Vue) {
    Vue.use(VueAxios, request)
  }
}

export default request

export {
  installer as VueAxios,
  request as axios
}
